import { Form } from 'antd'
import { CustomForm } from 'components/Forms/CustomForm'
import { Input } from '../Input'
import { FieldWithLabel } from '../FieldWithLabel/FieldWithLabel'
import { LABELS } from '../../constants'
import {
  TEditCommunityFeesProps,
  TEditCommunityFeesForm
} from './forms.editCommunityFees.d'
import { ReactComponent as ChevronDown16 } from 'assets/svg/ChevronDown16.svg'
import { ReactComponent as ChevronUp16 } from 'assets/svg/ChevronUp16.svg'
import './forms.editCommunityFees.scss'
import { percentageParser } from '../../features/ContractDetails/ContractDetailsGeneral/ContractDetailsForm/utils'
import { useCallback, useEffect } from 'react'
import { useSplitFeesPopup } from '../../features/Financials/SplitFees/Providers/SplitFeesPopupProvider'

export const EditCommunityFees = ({
  form,
  initialData,
  onSubmitFees,
  canEditGPOAllocation
}: TEditCommunityFeesProps) => {
  const {
    editLedByCommunityPopup: { state }
  } = useSplitFeesPopup()

  const health_systems = Form.useWatch('health_systems', form)

  const updateHsSplitFeesValues = useCallback(
    (formIdx, co_retain, hs_split) => [
      ...health_systems.map((hs, idx) =>
        formIdx === idx
          ? {
              ...hs,
              co_retain,
              hs_split
            }
          : hs
      )
    ],
    [health_systems]
  )

  const onHSSplitChange = useCallback(
    (value, formIdx) => {
      form.setFieldsValue({
        health_systems: updateHsSplitFeesValues(
          formIdx,
          (100 - Number(value)).toFixed(2),
          value
        )
      })
    },
    [form, updateHsSplitFeesValues]
  )

  const onCOSplitChange = useCallback(
    (value, formIdx) => {
      form.setFieldsValue({
        health_systems: updateHsSplitFeesValues(
          formIdx,
          value,
          (100 - Number(value)).toFixed(2)
        )
      })
    },
    [form, updateHsSplitFeesValues]
  )

  useEffect(() => {
    if (initialData && state.visible)
      form.setFieldsValue({
        gpo_allocation: initialData.gpo_allocation,
        health_systems: [...initialData.health_systems]
      })
  }, [form, initialData, state.visible])

  if (!initialData) return null

  return (
    <CustomForm<TEditCommunityFeesForm>
      id="edit-community-admin-fees"
      layout="vertical"
      className="edit-community-fees column gap-24"
      form={form}
      initialValues={initialData}
      onFinish={onSubmitFees}
    >
      {canEditGPOAllocation && (
        <Input.Number
          className="spinner-input-number"
          propsItem={{
            name: 'gpo_allocation',
            label: LABELS.GPO_ALLOCATION,
            rules: [
              {
                message: 'GPO allocation is required',
                // TODO: add condition to make field optional or hide the field if CO&HS have different GPO
                required: true
              }
            ]
          }}
          propsInputNumber={{
            controls: { downIcon: <ChevronDown16 />, upIcon: <ChevronUp16 /> },
            min: '0',
            max: '100',
            parser: percentageParser,
            placeholder: '0'
          }}
        />
      )}
      <Form.List name="health_systems">
        {(fields) =>
          fields.map((field) => (
            <div
              key={field.key}
              className="edit-community-fees__hs-data column gap-24"
            >
              <FieldWithLabel title="HS name">
                {initialData.health_systems[field.key]?.hs}
              </FieldWithLabel>
              <div className="row gap-24">
                <Input.Number
                  className="spinner-input-number"
                  propsItem={{
                    ...field,
                    name: [field.name, 'co_retain'],
                    label: LABELS.CO_RETAIN,
                    rules: [
                      {
                        message: 'CO retain is required',
                        required: true
                      }
                    ]
                  }}
                  propsInputNumber={{
                    controls: {
                      downIcon: <ChevronDown16 />,
                      upIcon: <ChevronUp16 />
                    },
                    min: '0',
                    max: '100',
                    parser: percentageParser,
                    placeholder: '0',
                    onChange: (val) => onCOSplitChange(val, field.name)
                  }}
                />
                <Input.Number
                  className="spinner-input-number"
                  propsItem={{
                    name: [field.name, 'hs_split'],
                    label: LABELS.HS_SPLIT,
                    rules: [
                      {
                        message: 'HS split is required',
                        required: true
                      }
                    ]
                  }}
                  propsInputNumber={{
                    controls: {
                      downIcon: <ChevronDown16 />,
                      upIcon: <ChevronUp16 />
                    },
                    min: '0',
                    max: '100',
                    parser: percentageParser,
                    placeholder: '0',
                    onChange: (val) => onHSSplitChange(val, field.name)
                  }}
                />
              </div>
            </div>
          ))
        }
      </Form.List>
    </CustomForm>
  )
}
